import type { SyncManager } from '@wiztime/sync';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonVariant } from '../../ui/Button';
import {
  AccountFailure,
  AccountFormFrame,
  EmailField,
  isValidEmail,
  PasswordField,
  passwordProblem,
  PasswordProblem,
  useAccountSubmit,
} from './accountForm';
import { usePassphraseMessage } from './JoinFlow';
import { checkPassphraseText, PassphraseCheckKind } from './passphraseCheck';
import styles from './Sync.module.css';
import { toConfigUrl } from './urls';

interface ResetFlowProps {
  readonly manager: SyncManager;
  readonly serverUrl: string;
  readonly onBack: () => void;
  /** Called with the email once the password was reset; the caller continues with the login view. */
  readonly onReset: (email: string) => void;
  readonly onUnsupported: () => void;
}

/** "Forgot password": set a new login password with the 12-word recovery code. */
export function ResetFlow({ manager, serverUrl, onBack, onReset, onUnsupported }: ResetFlowProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const submit = useAccountSubmit(onUnsupported);
  const check = useMemo(() => checkPassphraseText(code), [code]);
  const codeMessage = usePassphraseMessage(check);
  const codeOk = check.kind === PassphraseCheckKind.Valid;
  const codeBad =
    check.kind === PassphraseCheckKind.UnknownWord || check.kind === PassphraseCheckKind.Checksum;
  const problem = passwordProblem(password, confirm);
  const emailOk = isValidEmail(email);
  const valid = emailOk && codeOk && problem === null;

  const reset = async (): Promise<void> => {
    const done = await submit.run(async () => {
      await manager.resetPassword({
        serverUrl: toConfigUrl(serverUrl),
        email: email.trim(),
        recoveryCode: check.normalized ?? code,
        newPassword: password,
      });
      return true;
    });
    if (done === true) onReset(email.trim());
  };

  return (
    <AccountFormFrame
      title={t('sync.account.reset.title')}
      intro={t('sync.account.reset.intro')}
      busy={submit.busy}
      testId="account-reset"
      onSubmit={() => valid && void reset()}
      status={
        <AccountFailure failure={submit.failure} retryable={submit.retryable} onRetry={() => void reset()} />
      }
      actions={
        <>
          <Button disabled={submit.busy} onClick={onBack} data-testid="account-back">
            {t('sync.back')}
          </Button>
          <Button
            type="submit"
            variant={ButtonVariant.Primary}
            disabled={!valid || submit.busy}
            data-testid="account-submit"
          >
            {t('sync.account.reset.submit')}
          </Button>
        </>
      }
    >
      <EmailField
        label={t('sync.account.field.email')}
        value={email}
        onChange={setEmail}
        testId="account-email"
        invalid={email !== '' && !emailOk}
        message={email !== '' && !emailOk ? t('sync.account.invalid.email') : undefined}
      />
      <div className={styles.field}>
        <label htmlFor="account-recovery-code">{t('sync.account.field.recoveryCode')}</label>
        <textarea
          id="account-recovery-code"
          className={styles.passphraseInput}
          rows={3}
          value={code}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          aria-invalid={codeBad}
          aria-describedby="account-recovery-code-message"
          placeholder={t('sync.join.placeholder')}
          data-testid="account-recovery-code"
          onChange={(event) => setCode(event.target.value)}
        />
        <p
          id="account-recovery-code-message"
          className={codeBad ? styles.errorText : codeOk ? styles.good : styles.muted}
          aria-live="polite"
          data-testid="account-recovery-code-message"
        >
          {check.kind === PassphraseCheckKind.Empty ? t('sync.account.hint.recoveryCode') : codeMessage}
        </p>
      </div>
      <PasswordField
        label={t('sync.account.field.newPassword')}
        value={password}
        onChange={setPassword}
        testId="account-password"
        autoComplete="new-password"
        invalid={password !== '' && problem === PasswordProblem.Short}
        message={
          password !== '' && problem === PasswordProblem.Short
            ? t('sync.account.invalid.passwordShort')
            : t('sync.account.hint.password')
        }
      />
      <PasswordField
        label={t('sync.account.field.confirmNew')}
        value={confirm}
        onChange={setConfirm}
        testId="account-password-confirm"
        autoComplete="new-password"
        invalid={confirm !== '' && password !== confirm}
        message={confirm !== '' && password !== confirm ? t('sync.account.invalid.mismatch') : undefined}
      />
    </AccountFormFrame>
  );
}
